import React, { Fragment, MouseEvent, useEffect, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  CSSObject,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  styled,
  Theme,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import logoSmall from '@stack/assets/images/logo-icon.svg';
import logoSmallLight from '@stack/assets/images/logo-icon-light.svg';
import logo from '@stack/assets/images/logo-text.svg';
import logoLight from '@stack/assets/images/logo-text-light.svg';
import { useThemeSettings } from '@webbyx/mui';
import { redirect } from '@webbyx/next-js';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { AvatarTooltip } from '../AvatarTooltip';

import { CustomDrawerProps, DrawerProps } from './props';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

// ======================= THEME OVERRIDE
const openedMixin = ({
  theme,
  drawerwidth,
}: {
  theme: Theme;
  drawerwidth: number;
}): CSSObject => ({
  width: drawerwidth,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme: Theme): CSSObject => ({
  overflowX: 'hidden',
  width: `calc(${theme.spacing(20)})`,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

// ======================= Custom Drawer
const CustomDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<CustomDrawerProps>(({ theme, open, drawerwidth }) => ({
  width: drawerwidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin({ theme, drawerwidth }),
    '& .MuiDrawer-paper': openedMixin({ theme, drawerwidth }),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export const Drawer: React.FC<DrawerProps> = (props) => {
  const {
    handleDrawerToggle,
    items,
    drawerWidth = 0,
    open = false,
    menuButtonDisabled = false,
  } = props;

  // ======================= HOOKS
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xl')
  );
  const { settings } = useThemeSettings();

  // ======================= STATE
  const [subMenuOpen, setSubMenuOpen] = useState<Record<string, boolean>>({});
  const [anchorEl, setAnchorEl] = React.useState<{
    key: string;
    element: any;
  } | null>(null);

  // ======================= EVENTS
  useEffect(() => {
    items.forEach((item) => {
      if (item.subMenuItems) {
        setSubMenuOpen((prev) => {
          return {
            ...prev,
            [item.key]: router.route.split('/')[1] === item.key,
          };
        });
      }
    });
  }, [items, router.route]);

  const handleOnClick = (key: string, subPath?: string) => {
    if (subMenuOpen[key] === undefined) {
      redirect({}, `/${key}`, 'push');
      return;
    }
    if (subPath) {
      if (subPath === '/') {
        redirect({}, `/${key}`, 'push');
        return;
      }
      redirect({}, `/${key}/${subPath}`, 'push');
      return;
    }
    setSubMenuOpen((prev) => {
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  const handleMouseEnter = (
    event: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLDivElement>,
    key: string
  ) => {
    setAnchorEl({ key, element: event.currentTarget });
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  // ======================= VARIABLES

  // ======================= RENDER FUNCTIONS
  const renderExpandIcon = (key: string) => {
    if (subMenuOpen[key] === undefined) return;
    return subMenuOpen[key] ? <ExpandLess /> : <ExpandMore />;
  };

  const renderDrawerItem = (item: any) => {
    if (!isMobile && !open) {
      return (
        <Fragment key={item.key}>
          {subMenuOpen[item.key] === undefined ? (
            <AvatarTooltip title={item.label}>
              <ListItem sx={{ px: 3, py: 0, mb: 2 }}>
                <ListItemButton
                  disabled={menuButtonDisabled}
                  selected={
                    !item.subMenuItems &&
                    router.route.split('/')[1] === item.key
                  }
                  onClick={() => handleOnClick(item.key)}
                  sx={{
                    borderRadius: 2,
                    color: '#666666',
                    '&.Mui-selected, &.Mui-selected > .MuiListItemIcon-root, &.Mui-selected > .MuiListItemText-root > span':
                      {
                        backgroundColor: '#F5F6FA',
                        // color: theme.palette.accent.main,
                        color: '#EC6C0C',
                        fontWeight: 700,
                      },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#F5F6FA',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 2,
                      color: '#A0A0A0',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {renderExpandIcon(item.key)}
                </ListItemButton>
              </ListItem>
            </AvatarTooltip>
          ) : (
            <ListItem sx={{ px: 3, py: 0, mb: 2 }}>
              <ListItemButton
                disabled={menuButtonDisabled}
                selected={router.route.split('/')[1] === item.key}
                onMouseEnter={(
                  event:
                    | MouseEvent<HTMLAnchorElement>
                    | MouseEvent<HTMLDivElement>
                ) => handleMouseEnter(event, item.key)}
                sx={{
                  borderRadius: 2,
                  color: '#666666',
                  '&.Mui-selected, &.Mui-selected > .MuiListItemIcon-root, &.Mui-selected > .MuiListItemText-root > span':
                    {
                      backgroundColor: '#F5F6FA',
                      // color: theme.palette.accent.main,
                      color: '#EC6C0C',
                      fontWeight: 700,
                    },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 2,
                    color: '#A0A0A0',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </ListItemButton>
              <Popover
                open={anchorEl?.key === item.key}
                anchorEl={anchorEl?.element}
                onClose={handleMouseLeave}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <MenuList onMouseLeave={handleMouseLeave}>
                  {item.subMenuItems?.map((subItem) => (
                    <MenuItem
                      sx={{
                        py: 3,
                        borderRadius: 2,
                        color: '#666666',
                        '&.Mui-selected, &.Mui-selected > .MuiListItemIcon-root, &.Mui-selected > .MuiListItemText-root > span':
                          {
                            backgroundColor: '#F5F6FA',
                            color: '#EC6C0C',
                            fontWeight: 700,
                          },
                        '&.Mui-selected:hover': {
                          backgroundColor: '#F5F6FA',
                        },
                      }}
                      selected={
                        subItem.key === '/'
                          ? !router.route.split('/')[2] &&
                            router.route.split('/')[1] === item.key
                          : router.route.split('/')[2] === subItem.key
                      }
                      key={`${item.key}-${subItem.key}`}
                      onClick={() => {
                        handleMouseLeave();
                        handleOnClick(item.key, subItem.key);
                      }}
                    >
                      {subItem.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Popover>
            </ListItem>
          )}
        </Fragment>
      );
    }
    return (
      <Fragment key={item.key}>
        <ListItem sx={{ px: 3, py: 0, mb: 2 }}>
          <ListItemButton
            disabled={menuButtonDisabled}
            selected={
              !item.subMenuItems && router.route.split('/')[1] === item.key
            }
            onClick={() => handleOnClick(item.key)}
            sx={{
              borderRadius: 2,
              color: '#666666',
              '&.Mui-selected, &.Mui-selected > .MuiListItemIcon-root, &.Mui-selected > .MuiListItemText-root > span':
                {
                  backgroundColor: '#F5F6FA',
                  // color: theme.palette.accent.main,
                  color: '#EC6C0C',
                  fontWeight: 700,
                },
              '&.Mui-selected:hover': {
                backgroundColor: '#F5F6FA',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 2,
                color: '#A0A0A0',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{
                textTransform: 'uppercase',
                opacity: open ? 1 : 0,
              }}
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: 14,
                noWrap: true,
                color: 'secondary',
              }}
            />
            {renderExpandIcon(item.key)}
          </ListItemButton>
        </ListItem>
        <Collapse in={subMenuOpen[item.key]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subMenuItems?.map((subItem) => (
              <ListItem
                sx={{ px: 3, py: 0, mb: 2 }}
                key={`${item.key}-${subItem.key}`}
              >
                <ListItemButton
                  onClick={() => handleOnClick(item.key, subItem.key)}
                  selected={
                    subItem.key === '/'
                      ? !router.route.split('/')[2] &&
                        router.route.split('/')[1] === item.key
                      : router.route.split('/')[2] === subItem.key
                  }
                  sx={{
                    borderRadius: 2,
                    color: '#666666',
                    '&.Mui-selected, &.Mui-selected > .MuiListItemIcon-root, &.Mui-selected > .MuiListItemText-root > span':
                      {
                        backgroundColor: '#F5F6FA',
                        // color: theme.palette.accent.main,
                        color: '#EC6C0C',
                        fontWeight: 700,
                      },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#F5F6FA',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 4,
                      ml: 2,
                      color: '#A0A0A0',
                    }}
                  >
                    {subItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={subItem.label}
                    sx={{
                      textTransform: 'uppercase',
                    }}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: 14,
                      noWrap: true,
                      color: 'secondary',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Fragment>
    );
  };
  const drawer = (
    <>
      <DrawerHeader sx={{ paddingLeft: 3 }}>
        <Toolbar>
          {open ? (
            <Image
              src={settings.mode === 'light' ? logo : logoLight}
              alt={'logo'}
              priority
              onClick={() => redirect({}, '/', 'push')}
              style={{
                cursor: 'pointer',
              }}
            />
          ) : (
            <Image
              src={settings.mode === 'light' ? logoSmall : logoSmallLight}
              alt={'logo'}
              priority
              onClick={() => redirect({}, '/', 'push')}
              style={{
                cursor: 'pointer',
              }}
            />
          )}
        </Toolbar>
        {open ? (
          <IconButton
            sx={{
              display: { xs: 'none', xl: 'flex' },
              height: '46px',
              width: '46px',
              alignItems: 'center',
            }}
            onClick={handleDrawerToggle}
          >
            <ChevronLeftIcon />
          </IconButton>
        ) : null}
      </DrawerHeader>
      <List component="nav">{items.map((item) => renderDrawerItem(item))}</List>
    </>
  );

  const renderDrawer = () => {
    if (isMobile) {
      return (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', xl: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </MuiDrawer>
      );
    }
    return (
      <CustomDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', xl: 'block' },
        }}
        open={open}
        drawerwidth={drawerWidth}
      >
        {drawer}
      </CustomDrawer>
    );
  };

  // ======================= VIEWS
  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { xl: 0 },
      }}
    >
      {renderDrawer()}
    </Box>
  );
};

// ======================= EXPORT
export default Drawer;
